<template lang="pug">
  CrmBoardUi(
    :funnels="funnels"
    :funnel="funnel"
    :stages="stages"
    :leads="leads"
    :users="users"
    :serviceTypes="serviceTypes"
    :canManage="isCompanyManager"
    @update:funnel="funnel = $event"
    @addLead="addLead"
    @openMembers="openMembers"
    @openFunnelForm="openFunnelForm"
    v-slot="{ leads: visibleLeads, columns }"
    )
    CrmKanbanApi(
      :columns="columns"
      :leads="visibleLeads"
      :reasons="reasons"
      :canManage="isCompanyManager"
      @leadChanged="onLeadChanged"
      @leadRemoved="onLeadRemoved"
      @openLead="openLead"
      )
</template>

<script>
import popupModel from '@/model/popup';
import crmModel from '@/model/crm';
import form from '@/utils/form';

import companyApi from '@/api/company';
import crmFunnelApi from '@/api/crmFunnel';
import crmStageApi from '@/api/crmStage';
import CrmClientDir from '@/config/dir/CrmClient';

import BaseFormPopupWrappable from '@/components/base/BaseFormPopupWrappable.vue';
import CrmBoardUi from '@/components/crm/CrmBoardUi.vue';
import CrmKanbanApi from '@/components/crm/CrmKanbanApi.vue';
import CrmFunnelPopupWrappable from '@/components/crm/CrmFunnelPopupWrappable.vue';
import CrmFunnelMemberListPopupWrappable from '@/components/crm/CrmFunnelMemberListPopupWrappable.vue';
import CrmLeadDetailDrawerWrappable from '@/components/crm/CrmLeadDetailDrawerWrappable.vue';

/**
 * Заводит промис готовности данных. Не метод компонента: нужен уже в
 * beforeCreate, где methods ещё не инициализированы.
 */
function resetDataReady(vm) {
  vm.dataReady = new Promise((resolve) => { vm.resolveDataReady = resolve; });
}

/**
 * Данные доски и всё, что их меняет: загрузка воронки с заявками, права и
 * четыре попапа (заявка, участники, настройка воронки, форма новой заявки).
 *
 * Роутингом не занимается — про открытую заявку узнаёт пропами funnelId и
 * leadId, а о клике по карточке сообщает наверх событием: адресами владеет
 * страница.
 */
export default {
  name: 'CrmBoardApi',
  components: { CrmBoardUi, CrmKanbanApi },
  props: {
    companyId: {
      type: [Number, String],
      required: true,
    },
    // воронка заявки, открытой по адресу; null — заявка не открыта, и воронку
    // выбирают на самой доске. В адресе она стоит потому, что у заявки нет
    // своего поля funnel — по одной заявке доска не узнала бы, что показывать
    funnelId: {
      type: [Number, String],
      default: null,
    },
    // заявка, открытая по адресу; null — доска без попапа
    leadId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      funnel: null,
      funnels: [],
      // компания нужна для списка ответственных (владелец и замы работают с
      // любой воронкой) и для прав модерации комментариев
      company: null,
      stages: [],
      reasons: [],
      // типы услуг воронки: выбираются в заявке, по ним же фильтруют доску
      serviceTypes: [],
      leads: [],
      // кого можно назначить ответственным и по кому фильтровать
      users: [],
      // заявка, попап которой уже открыт, — чтобы watcher не открывал его повторно
      openedLeadId: null,
    };
  },
  /**
   * Попап заявки открывается по пропу leadId, и его watcher (immediate)
   * срабатывает раньше created — то есть раньше, чем загружены компания и
   * воронка. По прямой ссылке /crm/leads/{id} попап иначе открывался бы с
   * пустыми этапами и списком ответственных, а владелец компании выглядел бы
   * как участник воронки: без прав, которые считаются по загруженной компании.
   * Поэтому открытие ждёт этот промис. Заводим его в beforeCreate: к моменту
   * инициализации watcher'ов он уже нужен, а реактивным быть не должен.
   *
   * Счётчики поколений загрузки: и список воронок, и данные выбранной воронки
   * грузят несколько независимых путей (watcher воронки, колбэк формы
   * настройки, закрытие её попапа), никто из них друг друга не ждёт — без
   * счётчика ответ отменённой загрузки мог бы приехать последним и оставить на
   * доске этапы и заявки предыдущей воронки.
   *
   * Счётчика два, по одному на загрузку: они грузят разное и отменять должны
   * только сами себя. С общим счётчиком смена воронки (её watcher срабатывает
   * отложенно, уже после старта loadFunnels) отменяла бы заодно и загрузку
   * списка воронок — и список оставался бы прежним.
   */
  beforeCreate() {
    this.funnelsLoadId = 0;
    this.funnelDataLoadId = 0;
    resetDataReady(this);
  },
  created() {
    // Открытая шторка заявки и признак уничтожения. Оба нужны beforeDestroy и
    // ни на что в разметке не влияют, поэтому не в data
    this.leadDrawer = null;
    this.destroyed = false;

    this.loadFunnels();
  },
  /**
   * Шторка заявки лежит в общем сторе попапов, а не в разметке этого компонента,
   * поэтому уход со страницы сам её не убирает: из шторки можно уйти по ссылке
   * на проект, и она осталась бы висеть поверх открывшейся страницы.
   */
  beforeDestroy() {
    this.destroyed = true;
    if (this.leadDrawer) popupModel.resolve(this.leadDrawer, false);
  },
  watch: {
    companyId() {
      // прошлый промис уже разрешён данными прежней компании, а ждать теперь
      // нужно заново — иначе попап заявки, открытый по прямой ссылке сразу
      // после смены компании, получил бы этапы, ответственных и права от старой
      resetDataReady(this);
      this.funnel = null;
      this.loadFunnels();
    },
    funnel() {
      this.loadFunnelData();
    },
    /**
     * Ссылка на заявку другой воронки может прийти и на уже открытую страницу
     * — из ленты уведомлений в шапке. Тогда воронку переключаем здесь, а
     * первичный выбор по адресу делает loadFunnels (не immediate: списка
     * воронок в этот момент ещё нет).
     *
     * Промис готовности пересоздаём до смены воронки: старый уже разрешён
     * данными прежней, и шторка, открывающаяся по этому же переходу, иначе
     * получила бы чужие этапы. Watcher стоит выше leadId намеренно: Vue
     * запускает их в порядке объявления, и к открытию шторки воронка уже
     * должна быть сменена.
     */
    funnelId(funnelId) {
      const id = Number(funnelId);
      if (!id || id === this.funnel || !this.funnels.some(f => f.id === id)) return;

      resetDataReady(this);
      this.funnel = id;
    },
    leadId: {
      immediate: true,
      handler(leadId) {
        if (leadId) this.openLeadDrawer(Number(leadId));
      },
    },
  },
  computed: {
    currentUser() { return this.$store.state.auth.user; },
    /**
     * Управлять воронками (создание, настройка этапов и причин отказа, состав
     * участников), править клиентов и удалять чужие комментарии могут только
     * владелец компании и заместитель; участнику воронки всё это недоступно,
     * поэтому соответствующие кнопки ему не показываем.
     */
    isCompanyManager() {
      return crmModel.isCompanyManager(this.company, this.currentUser);
    },
    canEditClient() {
      return client => crmModel.canEditClient(
        client,
        { isManager: this.isCompanyManager, user: this.currentUser },
      );
    },
    /** Удалять клиентов может только владелец компании и заместитель (п. 2.5). */
    canRemoveClient() {
      return () => this.isCompanyManager;
    },
  },
  methods: {
    async loadFunnels() {
      // Промис готовности здесь НЕ пересоздаём: к первому вызову (из created)
      // его уже ждёт попап заявки, открытой по прямой ссылке — watcher пропа
      // с immediate срабатывает раньше created. Подменить промис сейчас значит
      // оставить того, кто ждёт, с промисом, который никто уже не разрешит.
      // Пересоздаёт его только watcher смены компании — там ожидание и правда
      // начинается заново.
      this.funnelsLoadId += 1;
      const token = this.funnelsLoadId;

      // Компанию тоже грузим с прелоадером: загрузка воронки стартует только
      // когда ответили оба запроса, и если компания отвечает последней, без
      // прелоадера на ней он успевает погаснуть и зажечься снова на воронке
      const [result, company] = await Promise.all([
        crmModel.run(
          () => crmFunnelApi.fetchList({ company: this.companyId }, true),
          'Не удалось загрузить список воронок',
          { pageLevel: true },
        ),
        crmModel.run(
          () => companyApi.fetchItem(this.companyId, {}, true),
          'Не удалось загрузить компанию',
          { pageLevel: true },
        ),
      ]);
      if (token !== this.funnelsLoadId) return;

      this.funnels = result?.items ?? [];
      this.company = company;

      // По прямой ссылке на заявку показываем её воронку. Иначе оставляем
      // выбранную — она могла быть удалена в форме настройки, тогда первую
      const wanted = Number(this.funnelId) || this.funnel;
      this.funnel = this.funnels.some(f => f.id === wanted)
        ? wanted
        : (this.funnels[0]?.id ?? null);

      // список воронок сменился — кандидатов в авторы клиента соберём заново
      crmModel.forgetClientOwners(this.companyId);

      // воронок нет — loadFunnelData не вызовется (watcher не сработает),
      // так что ждать больше нечего
      if (!this.funnel) this.resolveDataReady();
    },

    async loadFunnelData() {
      this.funnelDataLoadId += 1;
      const token = this.funnelDataLoadId;

      if (!this.funnel) {
        this.stages = [];
        this.reasons = [];
        this.serviceTypes = [];
        this.leads = [];
        this.users = [];
        this.resolveDataReady();
        return;
      }

      const funnel = await crmModel.run(
        () => crmModel.fetchFunnel(this.funnel),
        'Не удалось загрузить воронку',
        { pageLevel: true },
      );
      // пока грузили, успели выбрать другую воронку или сменить компанию —
      // этот ответ уже не о том, что показано на экране
      if (token !== this.funnelDataLoadId) return;
      if (!funnel) {
        this.resolveDataReady();
        return;
      }

      // этапы, причины отказа и типы услуг приходят вложенными в саму воронку
      this.stages = crmModel.funnelStages(funnel);
      this.reasons = crmModel.funnelReasons(funnel);
      this.serviceTypes = crmModel.funnelServiceTypes(funnel);

      const leads = await crmModel.run(
        () => crmModel.fetchLeads(this.funnel),
        'Не удалось загрузить заявки',
        { pageLevel: true },
      );
      if (token !== this.funnelDataLoadId) return;

      this.leads = leads ?? [];
      this.users = crmModel.assignableUsers(this.company, funnel);
      this.resolveDataReady();
    },

    onLeadChanged(lead) {
      const index = this.leads.findIndex(l => l.id === lead.id);
      if (index !== -1) this.$set(this.leads, index, lead);
    },
    onLeadRemoved(leadId) {
      this.leads = this.leads.filter(lead => lead.id !== leadId);
    },

    /** Поля формы новой заявки. Этап не спрашиваем — см. addLead. */
    leadFields(clientOwners) {
      return {
        title: form.createFieldObject('text', 'Название заявки', '', { required: 'Укажите название заявки' }),
        // клиента может завести любой, у кого есть доступ к CRM компании, а
        // вот править — только автор и руководящий состав, удалять — только
        // руководящий состав (п. 2.5)
        client: form.createDirFieldObject('Клиент', new CrmClientDir(), {
          filter: { company: Number(this.companyId) },
          context: { owners: clientOwners },
          constraints: { required: 'Выберите клиента' },
          canEdit: this.canEditClient,
          canRemove: this.canRemoveClient,
        }),
        manager: form.createUserFieldObject('Ответственный', this.users, {
          defaultValue: this.currentUser?.['@id'] ?? null,
          constraints: { required: 'Выберите ответственного' },
          emptyLabel: 'Нет доступных сотрудников',
        }),
      };
    },

    async addLead() {
      // заявка попадает в первый этап воронки по порядку сортировки
      const [firstStage] = this.stages;
      if (!firstStage) return;

      // клиента можно завести прямо из формы заявки, а его форме нужны
      // кандидаты в авторы
      const clientOwners = await crmModel.clientOwners(this.companyId);

      const values = await popupModel.add({
        wrappable: true,
        component: BaseFormPopupWrappable,
        params: { title: 'Новая заявка', fields: this.leadFields(clientOwners), values: {} },
      });
      if (!values) return;

      const lead = await crmModel.run(
        () => crmModel.createLead({ ...values, stage: crmStageApi.getIri(firstStage.id) }),
        'Не удалось создать заявку',
      );
      if (!lead) return;

      this.leads.push(lead);
      // сразу открываем деталку: у только что созданной заявки заполнено одно
      // название с клиентом, а тип услуги, описание и вложения дозаполняют уже
      // в ней
      this.openLead(lead.id);
    },

    /**
     * Наверх, а не openLeadDrawer напрямую — шторку открывает роут. Вместе с
     * заявкой уходит текущая воронка: она стоит в адресе заявки.
     */
    openLead(leadId) {
      this.$emit('openLead', { leadId, funnelId: this.funnel });
    },

    async openLeadDrawer(leadId) {
      if (this.openedLeadId === leadId) return;
      this.openedLeadId = leadId;

      // по прямой ссылке на заявку доска ещё грузится: шторке нужны и этапы с
      // ответственными, и права, посчитанные по компании
      await this.dataReady;

      // ссылку на шторку держим, чтобы закрыть её самим при уходе со страницы
      this.leadDrawer = {
        drawer: true,
        component: CrmLeadDetailDrawerWrappable,
        params: {
          leadId,
          companyId: this.companyId,
          // этапы — и для колонок в самой заявке, и истории для цвета плашек
          stages: this.stages,
          // причины отказа: заявку можно перевести в «Отказ» и отсюда
          reasons: this.reasons,
          users: this.users,
          serviceTypes: this.serviceTypes,
          canManage: this.isCompanyManager,
        },
        // отступы и фон деталка задаёт сама: она тянется на всю шторку
        options: { noPadding: true, background: '#F8F9FC' },
      };
      await popupModel.add(this.leadDrawer);

      this.leadDrawer = null;
      this.openedLeadId = null;
      // шторка закрылась вместе со страницей: сообщать наверх и перечитывать
      // заявку уже некому и незачем
      if (this.destroyed) return;

      this.$emit('closeLead');
      // заявку могли отредактировать в шторке
      this.refreshLead(leadId);
    },

    /**
     * Без прелоадера: заявку перечитывают сразу после закрытия шторки, и её
     * оверлей к этому моменту уже угас. Общий прелоадер затемнил бы страницу
     * второй раз — между двумя затемнениями она успевала мигнуть светлым.
     * Ждать этого обновления пользователю всё равно незачем: карточка уже на
     * доске и просто освежается на месте.
     */
    async refreshLead(leadId) {
      if (!this.leads.some(l => l.id === leadId)) return;

      const lead = await crmModel.run(
        () => crmModel.fetchLead(leadId, false),
        'Не удалось обновить заявку',
      );
      if (lead) this.onLeadChanged(lead);
    },

    async openMembers() {
      await popupModel.add({
        wrappable: true,
        component: CrmFunnelMemberListPopupWrappable,
        params: {
          funnelId: this.funnel,
          companyId: this.companyId,
          canManage: this.isCompanyManager,
        },
      });

      // состав воронки могли поменять — а он входит и в кандидатов в авторы
      // клиента, и в список возможных ответственных
      crmModel.forgetClientOwners(this.companyId);

      const funnel = await crmModel.run(
        () => crmModel.fetchFunnel(this.funnel),
        'Не удалось загрузить воронку',
      );
      if (!funnel) return;

      this.users = crmModel.assignableUsers(this.company, funnel);
    },

    async openFunnelForm(funnelId) {
      await popupModel.add({
        wrappable: true,
        component: CrmFunnelPopupWrappable,
        params: {
          companyId: this.companyId,
          funnelId,
          // выпадающий список воронок правим сразу: форма сообщает о создании,
          // переименовании и удалении самой воронки. Смену выбранной воронки
          // подхватит watcher и перечитает её данные
          onChange: async (changedId) => {
            this.funnel = changedId ?? null;
            await this.loadFunnels();
          },
        },
      });

      // Про правки этапов и причин отказа форма наружу не сообщает — их там
      // может быть сколько угодно за один сеанс. Поэтому по закрытии
      // перечитываем воронку целиком; выбор колонок CrmBoardUi сохранит сам,
      // раз воронка не менялась
      await this.loadFunnelData();
    },
  },
};
</script>
